/**
 * RelatedLinksRail
 *
 * Renders the central internal-links model for a given source page.
 * Defaults to showing primary + secondary links; pass `compact` to
 * limit to primary only.
 */

import { Link } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { getInternalLinksFor } from "@/lib/seo/internalLinks";
import { trackInternalLink } from "@/lib/analytics";

/** Artikelsidor (sv/en) → tjänsteklick mäts som article_to_service_click. */
const isArticlePath = (p: string) =>
  /^\/kunskap(\/|$)/.test(p) || /^\/en\/knowledge(\/|$)/.test(p);


interface Props {
  sourcePath: string;
  title?: string;
  compact?: boolean;
  className?: string;
}

export function RelatedLinksRail({
  sourcePath,
  title = "Relaterat",
  compact = false,
  className,
}: Props) {
  const links = getInternalLinksFor(sourcePath, {
    minPriority: compact ? "primary" : "secondary",
  });
  if (links.length === 0) return null;

  return (
    <nav
      aria-label={title}
      data-aeo="related-links"
      className={cn("border-t border-foreground/10 py-10", className)}
    >
      <p className="mb-4 text-[11px] font-medium uppercase tracking-[0.18em] text-foreground/60">
        {title}
      </p>
      <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {links.map((l) => (
          <li key={`${l.sourcePath}->${l.targetPath}`}>
            <Link
              to={l.targetPath}
              onClick={() => {
                const ev = isArticlePath(sourcePath)
                  ? "article_to_service_click"
                  : "related_link_click";
                trackInternalLink(ev, {
                  location: "related_links_rail",
                  label: l.anchorText,
                  to: l.targetPath,
                  from: sourcePath,
                });
              }}
              className="group flex items-center justify-between gap-3 rounded-md px-3 py-3 text-sm text-foreground/85 transition hover:bg-foreground/[0.04] hover:text-foreground"
            >

              <span className="truncate">{l.anchorText}</span>
              <ArrowUpRight
                size={14}
                className="shrink-0 text-foreground/50 transition group-hover:text-foreground"
              />
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );
}
